// @ts-nocheck
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { CheckCircle2, Upload, Loader2 } from 'lucide-react';
import { deploymentsApi, type ValidationResult } from '../../../api/deploymentsApi';
import { useValidateDeploymentCSV } from '../../../hooks/useValidateDeploymentCSV';
import { useImportDeploymentItems } from '../../../hooks/useImportDeploymentItems';
import { downloadBlob } from '../../../utils/downloadBlob';

interface DeploymentCSVImportDialogProps {
  deploymentId: string;
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

type Step = 'upload' | 'validating' | 'preview' | 'importing' | 'complete';


export const DeploymentCSVImportDialog = ({
  deploymentId,
  open,
  onClose,
  onImported,
}: DeploymentCSVImportDialogProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('upload');
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [inserted, setInserted] = useState(0);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const handleDownloadTemplate = async () => {
    setTemplateError(null);
    setDownloadingTemplate(true);
    try {
      const blob = await deploymentsApi.downloadCsvTemplate(deploymentId, 30);
      downloadBlob(blob, 'deployment-template.csv');
    } catch (err: any) {
      setTemplateError(err?.message ?? t('Downloading the template failed'));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const { mutate: validateCSV, error: validateError } = useValidateDeploymentCSV(deploymentId, {
    onSuccess: (result) => {
      setValidation(result);
      setStep('preview');
    },
    onError: () => setStep('upload'),
  });

  const { mutate: importItems, error: importError } = useImportDeploymentItems(deploymentId, {
    onSuccess: (result) => {
      setInserted(result.inserted);
      setStep('complete');
      onImported();
    },
    onError: () => setStep('preview'),
  });

  const reset = () => {
    setStep('upload');
    setValidation(null);
    setInserted(0);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File) => {
    setStep('validating');
    validateCSV(file);
  };

  const handleImport = () => {
    if (!validation?.validItems.length) return;
    setStep('importing');
    importItems(validation.validItems);
  };

  const renderUpload = () => (
    <div className="space-y-4">
      <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors">
        <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <p className="text-sm text-gray-600 mb-3">{t('Drag and drop a CSV file or')}</p>
        <label className="inline-block">
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
          <Button type="button" variant="outline" asChild>
            <span>{t('Browse')}</span>
          </Button>
        </label>
      </div>
      <Button variant="link" size="sm" onClick={handleDownloadTemplate} disabled={downloadingTemplate}>
        {downloadingTemplate && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
        {t('Download the deployment CSV template')}
      </Button>
      {templateError && <p className="text-sm text-red-600">{templateError}</p>}
      {validateError && <p className="text-sm text-red-600">{(validateError as Error).message}</p>}
    </div>
  );

  const renderValidating = () => (
    <div className="py-8 text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
      <p className="text-sm text-gray-600">{t('Reading the CSV…')}</p>
      <p className="text-xs text-gray-500 mt-1">
        {t('The stores, furniture and brands will be checked at publication time.')}
      </p>
    </div>
  );

  const renderPreview = () => {
    if (!validation) return null;
    const { errors, warnings, validItems, totalRows } = validation;
    return (
      <div className="space-y-4 min-w-0 max-w-full">
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="bg-green-50 border border-green-200 rounded p-3">
            <p className="text-green-700 font-medium">{t('{{count}} valid', { count: validItems.length })}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="text-yellow-700 font-medium">{t('{{count}} warning', { count: warnings.length })}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded p-3">
            <p className="text-red-700 font-medium">{t('{{count}} error', { count: errors.length })}</p>
          </div>
        </div>

        <p className="text-xs text-gray-500">{t('Total lines processed: {{count}}', { count: totalRows })}</p>

        {errors.length > 0 && (
          <div className="max-h-40 overflow-y-auto border rounded p-3 bg-red-50">
            <p className="text-xs font-medium text-red-700 mb-2">{t('Errors')}</p>
            <ul className="space-y-1 text-xs text-red-600">
              {errors.slice(0, 20).map((e, i) => (
                <li key={i}>
                  {t('Line')} {e.line}{e.column ? ` — ${e.column}` : ''} : {e.message}
                </li>
              ))}
              {errors.length > 20 && <li>{t('… and {{count}} more', { count: errors.length - 20 })}</li>}
            </ul>
          </div>
        )}

        {validItems.length > 0 && (
          <div className="border rounded overflow-hidden min-w-0 max-w-full">
            <div className="text-xs font-medium bg-gray-50 px-3 py-2 border-b">{t('Preview (first 5 rows)')}</div>
            <div className="overflow-x-auto max-w-full">
              <table className="w-full text-xs whitespace-nowrap">
                <thead>
                  <tr className="bg-gray-50 text-gray-600">
                    <th className="px-2 py-1 text-left">{t('Sector')}</th>
                    <th className="px-2 py-1 text-left">{t('Banner')}</th>
                    <th className="px-2 py-1 text-left">{t('Point of sale')}</th>
                    <th className="px-2 py-1 text-left">{t('Brand')}</th>
                    <th className="px-2 py-1 text-left">{t('Furniture')}</th>
                    <th className="px-2 py-1 text-left">{t('Technician')}</th>
                    <th className="px-2 py-1 text-left">{t('Email')}</th>
                    <th className="px-2 py-1 text-left">{t('Date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {validItems.slice(0, 5).map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-2 py-1">{item.sector}</td>
                      <td className="px-2 py-1">{item.shop_sign}</td>
                      <td className="px-2 py-1">{item.store_name}</td>
                      <td className="px-2 py-1">{item.brand}</td>
                      <td className="px-2 py-1">{item.furniture_name}</td>
                      <td className="px-2 py-1">{item.technician_name}</td>
                      <td className="px-2 py-1">{item.technician_email}</td>
                      <td className="px-2 py-1">{item.scheduled_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {importError && <p className="text-sm text-red-600">{(importError as Error).message}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={reset}>{t('Restart')}</Button>
          <Button onClick={handleImport} disabled={validItems.length === 0}>
            {t('Import {{count}} item', { count: validItems.length })}
          </Button>
        </div>
      </div>
    );
  };

  const renderImporting = () => (
    <div className="py-8 text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3"></div>
      <p className="text-sm text-gray-600">{t('Saving in progress…')}</p>
    </div>
  );

  const renderComplete = () => (
    <div className="py-6 text-center space-y-3">
      <CheckCircle2 className="mx-auto h-12 w-12 text-green-500" />
      <p className="text-sm text-gray-700">{t('{{count}} item imported', { count: inserted })}</p>
      <Button onClick={handleClose}>{t('Close')}</Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('Import a CSV')}</DialogTitle>
          <DialogDescription>
            {step === 'upload' && t('Upload the CSV to import into this deployment')}
            {step === 'validating' && t('Reading the CSV (format only)')}
            {step === 'preview' && t('Check and confirm the import')}
            {step === 'importing' && t('Importing into the database')}
            {step === 'complete' && t('Done')}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && renderUpload()}
        {step === 'validating' && renderValidating()}
        {step === 'preview' && renderPreview()}
        {step === 'importing' && renderImporting()}
        {step === 'complete' && renderComplete()}
      </DialogContent>
    </Dialog>
  );
};
